import { Button, ButtonStyle } from '@atlas/components';
import { type FC, useMemo } from 'react';
import { Link, useParams } from 'wouter';

import { type CasinoCategory, categoriesRequest } from 'api/casino';
import DraggableScroll from 'components/DraggableScroll/DraggableScroll';
import Icon from 'components/Icon/Icon';
import { ButtonIconTypes, type Config, type GetFieldType } from 'config/types';
import { useConfig } from 'config/utils';
import { AuthStatus, useClientProfile, useLanguage } from 'store';
import { getMediaUrl } from 'utils/assets';
import { type CasinoGroupPathParam, CasinoSubpage } from 'utils/casino';
import { Translation, useTranslation } from 'utils/translations';
import useRequest from 'utils/useRequest';

import CategoriesListChip from './CategoriesListChip';
import ListLabel from './ListLabel';

import {
  type ActiveItem,
  type CasinoCategoriesProps,
  type Item,
  ItemType,
} from './types';

import styles from './CasinoCategories.module.scss';
import ChipIcon from './ChipIcon';

const emptyActiveItems: Array<ActiveItem> = [];

type CustomIcon = Exclude<
  GetFieldType<Config, 'config.siteConfig.casino.buttonIcons'>,
  undefined
>[number];

const getCustomIcon = (buttonIcon?: CustomIcon) => {
  if (buttonIcon?.useDefaultIcon) return;
  return buttonIcon?.icon;
};

const CasinoCategories: FC<CasinoCategoriesProps> = ({
  activeItems = emptyActiveItems,
  group,
}) => {
  const [{ current: language }] = useLanguage();
  const buttonIcons = useConfig('config.siteConfig.casino.buttonIcons') ?? [];

  const { subpage } = useParams<{
    subpage: CasinoSubpage;
    group: CasinoGroupPathParam;
  }>();

  const [{ status }] = useClientProfile();
  const allGames = useTranslation('all_games');
  const liveCasino = useTranslation('live_casino');

  const isFavorites = subpage === CasinoSubpage.FAVORITES;
  const isAuthorized = status === AuthStatus.signedIn;

  const activeItemIds = useMemo(
    () =>
      activeItems.map((item) =>
        item.type === ItemType.category ? item.id : item.type,
      ),
    [activeItems],
  );

  const ALL_GAMES_ITEM: Item = useMemo(() => {
    const buttonIcon = buttonIcons.find(
      (icon) => icon.type === ButtonIconTypes.ALL_GAMES,
    );

    return {
      id: 'all',
      name: allGames,
      type: ItemType.all,
      active: activeItemIds.includes(ItemType.all) && !isFavorites,
      icon: (
        <ChipIcon
          type={ButtonIconTypes.ALL_GAMES}
          customIcon={getCustomIcon(buttonIcon)}
          useDefaultIcon={buttonIcon?.useDefaultIcon}
        />
      ),
    };
  }, [allGames, activeItemIds, isFavorites, buttonIcons]);

  const LIVE_CASINO_ITEM: Item = useMemo(() => {
    const buttonIcon = buttonIcons.find(
      (icon) => icon.type === ButtonIconTypes.LIVE_CASINO,
    );

    return {
      id: 'live',
      name: liveCasino,
      type: ItemType.live,
      active: activeItemIds.includes(ItemType.live) && !isFavorites,
      icon: (
        <ChipIcon
          type={ButtonIconTypes.LIVE_CASINO}
          customIcon={getCustomIcon(buttonIcon)}
          useDefaultIcon={buttonIcon?.useDefaultIcon}
        />
      ),
    };
  }, [liveCasino, activeItemIds, isFavorites, buttonIcons]);

  const FAVORITE_GAMES_ITEM: Item = useMemo(() => {
    const buttonIcon = buttonIcons.find(
      (icon) => icon.type === ButtonIconTypes.FAVOURITES,
    );

    return {
      id: 'favorites',
      type: ItemType.favorites,
      active: isFavorites,
      icon: (
        <ChipIcon
          type={ButtonIconTypes.FAVOURITES}
          customIcon={getCustomIcon(buttonIcon)}
          useDefaultIcon={buttonIcon?.useDefaultIcon}
        />
      ),
    };
  }, [isFavorites, buttonIcons]);

  const { data } = useRequest(
    () =>
      categoriesRequest({
        query: { group },
        headers: { 'x-locale': language.code },
      }),
    [group, language],
  );

  const categories = useMemo<CasinoCategory[]>(
    () => (data?.categories ?? []).sort((a, b) => a.position - b.position),
    [data],
  );

  const itemsList = useMemo<Item[]>(
    () => [
      ...(isAuthorized ? [FAVORITE_GAMES_ITEM] : []),
      ALL_GAMES_ITEM,
      LIVE_CASINO_ITEM,
      ...categories.map<Item>(({ id, name, iconUrl }) => ({
        type: ItemType.category,
        id: String(id),
        name,
        active: activeItemIds.includes(String(id)),
        icon: iconUrl ? (
          <img
            src={getMediaUrl(iconUrl, 'game')}
            alt={name}
            className={styles.icon}
          />
        ) : undefined,
      })),
    ],
    [
      categories,
      ALL_GAMES_ITEM,
      FAVORITE_GAMES_ITEM,
      LIVE_CASINO_ITEM,
      activeItemIds,
      isAuthorized,
    ],
  );

  const activeItem = useMemo<Item>(
    () => itemsList.find(({ active }) => active) ?? ALL_GAMES_ITEM,
    [itemsList, ALL_GAMES_ITEM],
  );

  return (
    <div className={styles.wrapper}>
      <DraggableScroll className={styles['categories-list']}>
        {itemsList.map((item) => (
          <CategoriesListChip key={item.id} {...item} />
        ))}
      </DraggableScroll>
      <div className={styles['list-labels']}>
        <div className={styles['list-label']}>
          <ListLabel activeItem={activeItem} />
        </div>
        {!isFavorites && (
          <Link to="/game-providers">
            <Button
              style={ButtonStyle.outline}
              icon={<Icon name="filter.bold" />}
            >
              <Translation message="providers_button" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default CasinoCategories;
